/**
 * Settled-pick outcome classification for season pools
 * (specs/competitions-hub-client Slice 4, Story 12).
 *
 * Mirrors the World Cup derivation at `components/worldcup/MatchesView/MatchCard.tsx`:
 * the CLASSIFICATION is computed on the client from pick vs final score, but the
 * POINTS are never fabricated — the server owns stage weighting and the golden-ball
 * ×2 and writes the result to `pointsEarned` at settlement. `pointsEarned == null`
 * on a finished match therefore means "still scoring", not zero.
 *
 * The overlay API (`competitionUserOverlay.helper.js`) sends only `pointsEarned`;
 * the `isExactHit` / `isCorrectResult` columns are not serialized, which is why this
 * derivation exists client-side rather than reading server flags.
 */

import type { ApiMatch } from '../../worldcup/helpers/worldcupPicksData.helper';

export type SeasonPickOutcome =
  /** Finished, picked, exact scoreline. */
  | 'exact'
  /** Finished, picked, right winner or draw but wrong scoreline. */
  | 'result'
  /** Finished, picked, wrong outcome. */
  | 'miss'
  /** Finished and picked, but the settlement sweep has not run yet. */
  | 'pending'
  /** Finished, never picked. */
  | 'no-pick'
  /** Not finished — the card is still editable or awaiting kickoff. */
  | 'unsettled';

/** -1 away win, 0 draw, 1 home win. */
function resultSign(home: number, away: number): -1 | 0 | 1 {
  if (home === away) return 0;
  return home > away ? 1 : -1;
}

/**
 * Classifies a season match card into exactly one outcome.
 *
 * Season fixtures never go to extra time, but `fullTime*` is preferred when present
 * so the season card judges on the same scoreline the server scored (matching the WC
 * card, where a knockout's final score includes ET and the pick is judged on 90').
 */
export function resolveSeasonPickOutcome(match: ApiMatch): SeasonPickOutcome {
  if (match.status !== 'finished') return 'unsettled';

  const prediction = match.prediction;
  if (prediction == null) return 'no-pick';

  const scoredHome = match.fullTimeHomeScore ?? match.homeScore;
  const scoredAway = match.fullTimeAwayScore ?? match.awayScore;
  if (scoredHome == null || scoredAway == null) return 'pending';

  // Settlement has not written a value yet — never render this as a zero-point miss.
  if (prediction.pointsEarned == null) return 'pending';

  if (prediction.home === scoredHome && prediction.away === scoredAway) return 'exact';
  if (resultSign(prediction.home, prediction.away) === resultSign(scoredHome, scoredAway)) {
    return 'result';
  }
  return 'miss';
}

/** True for the three outcomes that carry a colour + label treatment. */
export function isScoredOutcome(
  outcome: SeasonPickOutcome,
): outcome is 'exact' | 'result' | 'miss' {
  return outcome === 'exact' || outcome === 'result' || outcome === 'miss';
}
